import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";

import { Navigate, useSearchParams } from "react-router-dom";

import { PageHeader } from "@/components";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import { ROUTE_CONFIG } from "@/routes/config";
import { useAppDispatch } from "@/redux/hooks";
import { useGetScoreQuery } from "@/pages/Private/redux/score/score.api";
import { useGetFeaturesQuery } from "@/pages/Private/redux/feature/feature.api";
import { setKbEdited } from "@/pages/Private/redux/onboardingTour/onboardingTour.slice";

import {
	useGetIcpsQuery,
	useGetKnowledgeDbProductsQuery,
	useGetCaseStudiesQuery,
	useGetSettingsQuery,
	useGetObjectionCategoriesQuery,
	useGetObjectionsQuery,
} from "@/pages/Private/redux/knowledgeDb/knowledgeDb.api";

import { KnowledgeDbTab, SegmentKey, SegmentStatusFilter } from "./schema/knowledgeDb";
import {
	buildSegmentTree,
	apiProductToProduct,
	apiCaseStudyToCaseStudy,
	apiObjectionToObjection,
	apiCategoryToCategory,
	apiSettingToAutoUpdateSettings,
	computeTabStatuses,
} from "./utils/transformers";
import { useKnowledgeDbContents } from "./hooks/useKnowledgeDbContents";
import { useKnowledgeDbHandlers } from "./hooks/useKnowledgeDbHandlers";
import { usePendingSegmentSync } from "./hooks/usePendingSegmentSync";
import { KnowledgeDbTabs } from "./components/KnowledgeDbTabs";
import { SegmentSidebar } from "./components/SegmentSidebar";
import { ContentArea } from "./components/ContentArea";
import { ProductSidebar } from "./components/ProductSidebar";
import { ProductContentArea } from "./components/ProductContentArea";
import { AddProductModal } from "./components/AddProductModal";
import { CaseStudyAutoUpdate } from "./components/CaseStudyAutoUpdate";
import { CaseStudySidebar } from "./components/CaseStudySidebar";
import { CaseStudyContentArea } from "./components/CaseStudyContentArea";
import { AddCaseStudyModal } from "./components/AddCaseStudyModal";
import { ObjectionCategorySidebar } from "./components/ObjectionCategorySidebar";
import { ObjectionContentArea } from "./components/ObjectionContentArea";
import { AddObjectionModal } from "./components/AddObjectionModal";
import { AddCategoryModal } from "./components/AddCategoryModal";
import {
	KNOWLEDGE_DB_TOUR_PARAM,
	KnowledgeDbTourReturnBar,
} from "./components/KnowledgeDbTourReturnBar";
import { ModalSegmentSync } from "../Score/components/ModalSegmentSync";

export const KnowledgeDb: FunctionComponent = () => {
	const { isLoading: isFeaturesLoading } = useGetFeaturesQuery();
	const isKnowledgeDbActive = useIsFeatureActive(FeaturesCodes.KNOWLEDGE_DB);
	const dispatch = useAppDispatch();
	const [searchParams] = useSearchParams();

	// Visited from the onboarding tour (PREC-3700): flag every persisted edit in the tour slice.
	const isTourVisit = searchParams.get(KNOWLEDGE_DB_TOUR_PARAM) === "1";

	const handleMutated = useCallback(() => {
		if (isTourVisit) {
			dispatch(setKbEdited(true));
		}
	}, [isTourVisit, dispatch]);

	// ── API Queries ──
	const { data: scoreData, isLoading: isScoreLoading, isError: isScoreError } = useGetScoreQuery();
	const {
		data: icpsData,
		isLoading: isIcpsLoading,
		isError: isIcpsError,
	} = useGetIcpsQuery({ page: 1, limit: 10000 });
	const {
		data: productsData,
		isLoading: isProductsLoading,
		isError: isProductsError,
	} = useGetKnowledgeDbProductsQuery({ page: 1, limit: 10000 });
	const {
		data: caseStudiesData,
		isLoading: isCaseStudiesLoading,
		isError: isCaseStudiesError,
	} = useGetCaseStudiesQuery({ page: 1, limit: 10000 });
	const { data: settingsData } = useGetSettingsQuery();
	const {
		data: categoriesData,
		isLoading: isCategoriesLoading,
		isError: isCategoriesError,
	} = useGetObjectionCategoriesQuery({ page: 1, limit: 10000 });
	const {
		data: objectionsData,
		isLoading: isObjectionsLoading,
		isError: isObjectionsError,
	} = useGetObjectionsQuery({ page: 1, limit: 10000 });

	const isLoading =
		isScoreLoading ||
		isIcpsLoading ||
		isProductsLoading ||
		isCaseStudiesLoading ||
		isCategoriesLoading ||
		isObjectionsLoading;
	const isError =
		isScoreError ||
		isIcpsError ||
		isProductsError ||
		isCaseStudiesError ||
		isCategoriesError ||
		isObjectionsError;

	// ── Pending Segment Sync ──
	const { showSegmentSyncModal, pendingSyncChanges, pendingSyncId, closeSegmentSyncModal } =
		usePendingSegmentSync(!!isKnowledgeDbActive);

	// ── Transformed Data ──
	const companySegments = useMemo(
		() => (scoreData ? buildSegmentTree(scoreData) : []),
		[scoreData]
	);

	const { contents, visibleContents } = useKnowledgeDbContents(icpsData?.data, companySegments);

	const products = useMemo(
		() => (productsData?.data ?? []).map(apiProductToProduct),
		[productsData]
	);

	const caseStudies = useMemo(
		() => (caseStudiesData?.data ?? []).map(apiCaseStudyToCaseStudy),
		[caseStudiesData]
	);

	const autoUpdateSettings = useMemo(
		() => (settingsData ? apiSettingToAutoUpdateSettings(settingsData) : null),
		[settingsData]
	);

	const objectionCategories = useMemo(
		() => (categoriesData?.data ?? []).map(apiCategoryToCategory),
		[categoriesData]
	);

	const objections = useMemo(
		() => (objectionsData?.data ?? []).map(apiObjectionToObjection),
		[objectionsData]
	);

	// ── UI State ──
	const [activeTab, setActiveTab] = useState<KnowledgeDbTab>(KnowledgeDbTab.PAIN_POINTS);
	const [activeSegment, setActiveSegment] = useState<SegmentKey | null>(null);
	const [activeProductId, setActiveProductId] = useState<number | null>(null);
	const [isAddProductOpen, setIsAddProductOpen] = useState(false);
	const [activeCaseStudyId, setActiveCaseStudyId] = useState<number | null>(null);
	const [isAddCaseStudyOpen, setIsAddCaseStudyOpen] = useState(false);
	const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
	const [isAddObjectionOpen, setIsAddObjectionOpen] = useState(false);
	const [addObjectionDefaultCategoryId, setAddObjectionDefaultCategoryId] = useState<number | null>(
		null
	);
	const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
	const [statusFilter, setStatusFilter] = useState<SegmentStatusFilter>(SegmentStatusFilter.ALL);
	const [productStatusFilter, setProductStatusFilter] = useState<SegmentStatusFilter>(
		SegmentStatusFilter.ALL
	);
	const [caseStudyStatusFilter, setCaseStudyStatusFilter] = useState<SegmentStatusFilter>(
		SegmentStatusFilter.ALL
	);
	const [objectionStatusFilter, setObjectionStatusFilter] = useState<SegmentStatusFilter>(
		SegmentStatusFilter.ALL
	);

	// ── Mutations, handlers & bulk-approve selection (extracted hook) ──
	const {
		selectedSegmentIds,
		selectedProductIds,
		selectedCaseStudyIds,
		selectedObjectionIds,
		handleContentChange,
		handleToggleSegmentSelection,
		handleSelectAllSegments,
		handleBulkApprove,
		handleToggleProductSelection,
		handleSelectAllProducts,
		handleProductApprove,
		handleBulkApproveProducts,
		handleProductChange,
		handleProductUnapprove,
		handleProductDelete,
		handleCreateProduct,
		handleToggleCaseStudySelection,
		handleSelectAllCaseStudies,
		handleCaseStudyApprove,
		handleBulkApproveCaseStudies,
		handleCaseStudyChange,
		handleCaseStudyUnapprove,
		handleCaseStudyDelete,
		handleCreateCaseStudy,
		handleSettingsChange,
		handleTriggerManualUpdate,
		handleToggleObjectionSelection,
		handleSelectAllObjections,
		handleObjectionApprove,
		handleBulkApproveObjections,
		handleObjectionChange,
		handleObjectionUnapprove,
		handleObjectionDelete,
		handleCreateObjection,
		handleCreateCategory,
		handleDeleteCategory,
	} = useKnowledgeDbHandlers({
		objectionCategories,
		setActiveProductId,
		setActiveCaseStudyId,
		setActiveCategoryId,
		onMutated: handleMutated,
	});

	// ── Auto-select first items when data loads ──
	useEffect(() => {
		if (!activeSegment && companySegments.length > 0) {
			const firstPersona = companySegments[0]?.personas[0];

			if (firstPersona) {
				setActiveSegment({
					scorePersonaCategoryId: firstPersona.id,
					scoreCompanyCategoryId: companySegments[0].id,
				});
			}
		}
	}, [companySegments, activeSegment]);

	useEffect(() => {
		if (activeProductId === null && products.length > 0) {
			setActiveProductId(products[0].id);
		}
	}, [products, activeProductId]);

	useEffect(() => {
		if (activeCaseStudyId === null && caseStudies.length > 0) {
			setActiveCaseStudyId(caseStudies[0].id);
		}
	}, [caseStudies, activeCaseStudyId]);

	useEffect(() => {
		if (activeCategoryId === null && objectionCategories.length > 0) {
			setActiveCategoryId(objectionCategories[0].id);
		}
	}, [objectionCategories, activeCategoryId]);

	// ── Derived ──
	const activeContent = useMemo(() => {
		if (!activeSegment) {
			return undefined;
		}

		return contents.find(
			(c) =>
				c.scorePersonaCategoryId === activeSegment.scorePersonaCategoryId &&
				c.scoreCompanyCategoryId === activeSegment.scoreCompanyCategoryId
		);
	}, [activeSegment, contents]);

	const activeCompanySegment = useMemo(
		() => companySegments.find((cs) => cs.id === activeSegment?.scoreCompanyCategoryId),
		[companySegments, activeSegment]
	);

	const activePersonaSegment = useMemo(
		() =>
			activeCompanySegment?.personas.find((ps) => ps.id === activeSegment?.scorePersonaCategoryId),
		[activeCompanySegment, activeSegment]
	);

	// ── Computed Tab Statuses ──
	const tabStatuses = useMemo(
		() => computeTabStatuses({ contents: visibleContents, products, caseStudies, objections }),
		[visibleContents, products, caseStudies, objections]
	);

	const handleAddObjectionToCategory = useCallback((categoryId: number) => {
		setAddObjectionDefaultCategoryId(categoryId);
		setIsAddObjectionOpen(true);
	}, []);

	const isProductsTab = activeTab === KnowledgeDbTab.PRODUCTS_SERVICES;
	const isCaseStudiesTab = activeTab === KnowledgeDbTab.CASE_STUDIES;
	const isObjectionHandlingTab = activeTab === KnowledgeDbTab.OBJECTION_HANDLING;

	if (isFeaturesLoading) {
		return (
			<div className="flex flex-1 items-center justify-center">
				<div className="flex flex-col items-center gap-3">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-light" />
					<p className="text-sm text-gray-500">Loading…</p>
				</div>
			</div>
		);
	}

	if (!isKnowledgeDbActive) {
		return <Navigate to={ROUTE_CONFIG.LISTS} />;
	}

	return (
		<div className="flex flex-col h-full w-full">
			{/* Page header with the tab strip directly below the title */}
			<PageHeader title="Knowledge DB">
				<KnowledgeDbTabs
					activeTab={activeTab}
					tabStatuses={tabStatuses}
					onTabChange={setActiveTab}
				/>
			</PageHeader>

			{/* Main content area */}
			{isLoading ? (
				<div className="flex flex-1 items-center justify-center">
					<div className="flex flex-col items-center gap-3">
						<div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-light" />
						<p className="text-sm text-gray-500">Loading knowledge base data…</p>
					</div>
				</div>
			) : isError ? (
				<div className="flex flex-1 items-center justify-center">
					<div className="flex flex-col items-center gap-2 text-center">
						<p className="text-sm font-medium text-red-600">Failed to load knowledge base data.</p>
						<p className="text-xs text-gray-500">Please try refreshing the page.</p>
					</div>
				</div>
			) : isCaseStudiesTab ? (
				<div className="flex flex-col flex-1 overflow-hidden">
					{autoUpdateSettings && (
						<CaseStudyAutoUpdate
							settings={autoUpdateSettings}
							onSettingsChange={handleSettingsChange}
							onTriggerManualUpdate={handleTriggerManualUpdate}
						/>
					)}
					<div className="flex flex-1 overflow-hidden">
						<CaseStudySidebar
							activeCaseStudyId={activeCaseStudyId}
							caseStudies={caseStudies}
							selectedCaseStudyIds={selectedCaseStudyIds}
							statusFilter={caseStudyStatusFilter}
							onAddCaseStudy={() => setIsAddCaseStudyOpen(true)}
							onBulkApprove={handleBulkApproveCaseStudies}
							onCaseStudySelect={setActiveCaseStudyId}
							onSelectAll={handleSelectAllCaseStudies}
							onStatusFilterChange={setCaseStudyStatusFilter}
							onToggleCaseStudySelection={handleToggleCaseStudySelection}
						/>
						<CaseStudyContentArea
							activeCaseStudyId={activeCaseStudyId}
							caseStudies={caseStudies}
							onCaseStudyApprove={handleCaseStudyApprove}
							onCaseStudyChange={handleCaseStudyChange}
							onCaseStudyDelete={handleCaseStudyDelete}
							onCaseStudyUnapprove={handleCaseStudyUnapprove}
						/>
					</div>
					<AddCaseStudyModal
						isOpen={isAddCaseStudyOpen}
						onClose={() => setIsAddCaseStudyOpen(false)}
						onCreate={handleCreateCaseStudy}
					/>
				</div>
			) : isObjectionHandlingTab ? (
				<div className="flex flex-1 overflow-hidden">
					<ObjectionCategorySidebar
						activeCategoryId={activeCategoryId}
						categories={objectionCategories}
						objections={objections}
						selectedObjectionIds={selectedObjectionIds}
						statusFilter={objectionStatusFilter}
						onAddCategory={() => setIsAddCategoryOpen(true)}
						onBulkApprove={handleBulkApproveObjections}
						onCategorySelect={setActiveCategoryId}
						onDeleteCategory={handleDeleteCategory}
						onSelectAll={handleSelectAllObjections}
						onStatusFilterChange={setObjectionStatusFilter}
						onToggleObjectionSelection={handleToggleObjectionSelection}
					/>
					<ObjectionContentArea
						activeCategoryId={activeCategoryId}
						categories={objectionCategories}
						objections={objections}
						onAddObjection={handleAddObjectionToCategory}
						onObjectionApprove={handleObjectionApprove}
						onObjectionChange={handleObjectionChange}
						onObjectionDelete={handleObjectionDelete}
						onObjectionUnapprove={handleObjectionUnapprove}
					/>
					<AddObjectionModal
						categories={objectionCategories}
						defaultCategoryId={addObjectionDefaultCategoryId}
						isOpen={isAddObjectionOpen}
						onClose={() => setIsAddObjectionOpen(false)}
						onCreateManual={(data) => handleCreateObjection(data, false)}
						onCreateWithAi={(data) => handleCreateObjection(data, true)}
					/>
					<AddCategoryModal
						isOpen={isAddCategoryOpen}
						onClose={() => setIsAddCategoryOpen(false)}
						onCreate={handleCreateCategory}
					/>
				</div>
			) : (
				<div className="flex flex-1 overflow-hidden">
					{isProductsTab ? (
						<>
							<ProductSidebar
								activeProductId={activeProductId}
								products={products}
								selectedProductIds={selectedProductIds}
								statusFilter={productStatusFilter}
								onAddProduct={() => setIsAddProductOpen(true)}
								onBulkApprove={handleBulkApproveProducts}
								onProductSelect={setActiveProductId}
								onSelectAll={handleSelectAllProducts}
								onStatusFilterChange={setProductStatusFilter}
								onToggleProductSelection={handleToggleProductSelection}
							/>
							<ProductContentArea
								activeProductId={activeProductId}
								products={products}
								onProductApprove={handleProductApprove}
								onProductChange={handleProductChange}
								onProductDelete={handleProductDelete}
								onProductUnapprove={handleProductUnapprove}
							/>
							<AddProductModal
								isOpen={isAddProductOpen}
								onClose={() => setIsAddProductOpen(false)}
								onCreateManual={(data) => handleCreateProduct(data, false)}
								onCreateWithAi={(data) => handleCreateProduct(data, true)}
							/>
						</>
					) : (
						<>
							<SegmentSidebar
								activeSegment={activeSegment}
								companySegments={companySegments}
								contents={contents}
								lastModified={icpsData?.data?.[0]?.updatedAt ?? ""}
								selectedSegmentIds={selectedSegmentIds}
								statusFilter={statusFilter}
								onBulkApprove={handleBulkApprove}
								onSegmentSelect={setActiveSegment}
								onSelectAll={handleSelectAllSegments}
								onStatusFilterChange={setStatusFilter}
								onToggleSegmentSelection={handleToggleSegmentSelection}
							/>
							<ContentArea
								activeSegment={activeSegment}
								companySegment={activeCompanySegment}
								content={activeContent}
								personaSegment={activePersonaSegment}
								onContentChange={handleContentChange}
							/>
						</>
					)}
				</div>
			)}
			<KnowledgeDbTourReturnBar />
			{showSegmentSyncModal && (
				<ModalSegmentSync
					changes={pendingSyncChanges}
					isOpened={showSegmentSyncModal}
					pendingSyncId={pendingSyncId}
					onClose={closeSegmentSyncModal}
				/>
			)}
		</div>
	);
};
